import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link, Globe, Download } from "lucide-react";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import JSZip from "jszip";

export default function WordPressIntegrationCard() {
  const { currentWebsite } = useWebsites();
  const { settings: wpSettings } = useWordPress();
  const { directWpSettings, setShowAuthDialog } = useSettingsState();
  const [usePlugin, setUsePlugin] = useState(true);
  const [pluginDownloaded, setPluginDownloaded] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const openWordPressPluginsPage = () => {
    if (!currentWebsite?.url) return;
    let cleanUrl = currentWebsite.url.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    cleanUrl = cleanUrl.replace(/\/+$/, '');
    const wpAddNewPluginUrl = `${cleanUrl}/wp-admin/plugin-install.php?tab=upload`;
    window.open(wpAddNewPluginUrl, '_blank');
  };

  const downloadPluginZip = async () => {
    try {
      const zip = new JSZip();
      const pluginFolder = zip.folder('contentgardener-connector');
      
      const pluginContent = `<?php
/**
 * Plugin Name: ContentGardener Connector
 * Description: Automatically creates an Application Password for ContentGardener integration
 * Version: 1.0.0
 * Author: ContentGardener
 */

// Prevent direct access
if (!defined('ABSPATH')) {
    exit;
}

class ContentGardenerConnector {
    
    public function __construct() {
        add_action('admin_menu', array($this, 'add_admin_menu'));
        add_action('admin_init', array($this, 'handle_activation'));
        register_activation_hook(__FILE__, array($this, 'activate_plugin'));
    }
    
    public function activate_plugin() {
        // Auto-create application password on activation
        $this->create_application_password();
    }
    
    public function handle_activation() {
        if (isset($_GET['page']) && $_GET['page'] === 'contentgardener-connector') {
            $this->create_application_password();
        }
    }
    
    private function create_application_password() {
        $user = wp_get_current_user();
        if (!$user->exists()) {
            return;
        }
        
        // Check if password already exists
        $app_passwords = WP_Application_Passwords::get_user_application_passwords($user->ID);
        foreach ($app_passwords as $app_password) {
            if ($app_password['name'] === 'ContentGardener') {
                return; // Already exists
            }
        }
        
        // Create new application password
        $password_data = WP_Application_Passwords::create_new_application_password($user->ID, array(
            'name' => 'ContentGardener',
            'capabilities' => array('read', 'edit_posts', 'publish_posts')
        ));
        
        if (is_wp_error($password_data)) {
            return;
        }
        
        // Store the password temporarily for display
        update_option('contentgardener_app_password', $password_data['password']);
    }
    
    public function add_admin_menu() {
        add_menu_page(
            'ContentGardener',
            'ContentGardener',
            'manage_options',
            'contentgardener-connector',
            array($this, 'admin_page'),
            'dashicons-admin-generic',
            30
        );
    }
    
    public function admin_page() {
        $user = wp_get_current_user();
        $app_password = get_option('contentgardener_app_password');
        
        echo '<div class="wrap">';
        echo '<h1>ContentGardener Connection Details</h1>';
        echo '<div class="notice notice-success">';
        echo '<p><strong>Your WordPress site is now ready to connect with ContentGardener!</strong></p>';
        echo '</div>';
        
        echo '<div class="card" style="max-width: 600px; padding: 20px; margin: 20px 0;">';
        echo '<h2>Connection Information</h2>';
        echo '<table class="form-table">';
        echo '<tr><th>WordPress URL:</th><td><code>' . esc_url(get_site_url()) . '</code></td></tr>';
        echo '<tr><th>Username:</th><td><code>' . esc_html($user->user_login) . '</code></td></tr>';
        if ($app_password) {
            echo '<tr><th>Application Password:</th><td><code>' . esc_html($app_password) . '</code></td></tr>';
        } else {
            echo '<tr><th>Application Password:</th><td><em>Not generated yet. Please refresh this page.</em></td></tr>';
        }
        echo '</table>';
        
        echo '<p><strong>Next steps:</strong></p>';
        echo '<ol>';
        echo '<li>Copy the WordPress URL, Username, and Application Password above</li>';
        echo '<li>Go to your ContentGardener dashboard</li>';
        echo '<li>Enter these details in the WordPress connection settings</li>';
        echo '</ol>';
        echo '</div>';
        echo '</div>';
    }
}

// Initialize the plugin
new ContentGardenerConnector();
?>`;
      
      pluginFolder?.file('contentgardener-connector.php', pluginContent);
      
      const readmeContent = `# ContentGardener Connector

A WordPress plugin that automatically creates an Application Password for ContentGardener integration.

## Installation

1. Upload this zip file to your WordPress site via Plugins → Add New → Upload Plugin
2. Activate the plugin
3. Go to the "ContentGardener" menu item in your WordPress admin
4. Copy the connection details shown on that page

## What it does

- Automatically creates an Application Password named "ContentGardener"
- Provides a simple interface to view connection details
- Handles the complex setup process for you

## Security

This plugin only creates an Application Password with the necessary permissions for content publishing. It does not store any sensitive data permanently.`;
      
      pluginFolder?.file('README.md', readmeContent);
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contentgardener-connector.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setPluginDownloaded(true);
      toast.success('Plugin downloaded successfully!');
    } catch (error) {
      console.error('Error creating plugin zip:', error);
      toast.error('Failed to download plugin');
    }
  };

  const handleWordPressConnection = async () => {
    if (!currentWebsite?.url) {
      toast.error('Website URL is not configured');
      return;
    }
    setShowInstructions(true);
  };

  return (
    <Card id="wordpress">
      <CardHeader>
        <CardTitle>WordPress integration</CardTitle>
        <CardDescription>
          {wpSettings?.is_connected || directWpSettings?.is_connected
            ? "Manage your WordPress connection and view recent activity."
            : "Connect your WordPress website to automatically publish content. We'll guide you through the process."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {wpSettings?.is_connected || directWpSettings?.is_connected ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <div>
                  <p className="font-medium text-green-800">Connected to WordPress</p>
                  <p className="text-sm text-green-600">
                    {wpSettings?.wp_url || directWpSettings?.wp_url}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Manage Connection
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <Button
              onClick={handleWordPressConnection}
              className="w-full"
            >
              <Link className="h-4 w-4 mr-2" />
              Choose Connection Method
            </Button>

            <div className="text-center">
              <p className="text-xs text-muted-foreground">
                Choose between our simple plugin setup or manual Application Password creation.
              </p>
            </div>

            {showInstructions && (
              <div className="border rounded-lg p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Connect WordPress</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInstructions(false)}
                  >
                    ✕
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground">
                  Choose your preferred method to connect your WordPress site
                </p>

                <div className="flex items-center justify-center p-4 bg-muted rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`text-sm font-medium transition-colors ${!usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                      Simple (Plugin)
                    </span>
                    <button
                      onClick={() => setUsePlugin(!usePlugin)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                        !usePlugin ? 'bg-primary' : 'bg-muted-foreground/20'
                      }`}
                      aria-label={`Switch to ${!usePlugin ? 'Advanced' : 'Simple'} mode`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          !usePlugin ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                    <span className={`text-sm font-medium transition-colors ${usePlugin ? 'text-primary' : 'text-muted-foreground'}`}>
                      Advanced (Manual)
                    </span>
                  </div>
                </div>

                {usePlugin && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">1</div>
                        <div>
                          <h3 className="font-medium">Download Plugin</h3>
                          <p className="text-sm text-muted-foreground">Download our WordPress plugin that handles everything automatically.</p>
                          <div className="mt-2">
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" onClick={downloadPluginZip}>
                                <Download className="mr-2 h-4 w-4" /> Download Plugin
                              </Button>
                              {pluginDownloaded && (
                                <Button variant="outline" size="sm" onClick={openWordPressPluginsPage}>
                                  <Globe className="mr-2 h-4 w-4" /> Open WordPress
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                                                   <div className="flex items-start gap-3">
                               <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">2</div>
                               <div>
                                 <h3 className="font-medium">Install & Activate Plugin</h3>
                                 <p className="text-sm text-muted-foreground">In your WordPress admin, go to Plugins → Add New → Upload Plugin and install the downloaded zip file.</p>
                                 <div className="mt-2">
                                   <div className="flex gap-2">
                                     <Button 
                                       variant="outline" 
                                       size="sm"
                                       onClick={() => {
                                         if (!currentWebsite?.url) return;
                                         let cleanUrl = currentWebsite.url.trim();
                                         if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
                                           cleanUrl = 'https://' + cleanUrl;
                                         }
                                         cleanUrl = cleanUrl.replace(/\/+$/, '');
                                         window.open(`${cleanUrl}/wp-admin/plugins.php`, '_blank');
                                       }}
                                     >
                                       <Globe className="mr-2 h-4 w-4" />
                                       Open Plugins Page
                                     </Button>
                                   </div>
                                 </div>
                                 <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                   <p className="text-sm text-amber-800">
                                     <strong>Important:</strong> After installing, make sure to <strong>activate the plugin</strong> by clicking "Activate" next to "ContentGardener Connector" in your plugins list.
                                   </p>
                                 </div>
                               </div>
                             </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">3</div>
                        <div>
                          <h3 className="font-medium">Get Connection Details</h3>
                          <p className="text-sm text-muted-foreground">After activation, go to "ContentGardener" in your WordPress admin menu and copy the connection details.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">4</div>
                        <div>
                          <h3 className="font-medium">Enter Connection Details</h3>
                          <p className="text-sm text-muted-foreground">Use the "Connect & Test" button below to enter the WordPress URL, username, and application password.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!usePlugin && (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">1</div>
                        <div>
                          <h3 className="font-medium">Open WordPress Admin</h3>
                          <p className="text-sm text-muted-foreground">Click the button below to open your WordPress admin panel in a new tab.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">2</div>
                        <div>
                          <h3 className="font-medium">Create Application Password</h3>
                          <p className="text-sm text-muted-foreground">In the WordPress admin, scroll down to "Application Passwords" section. Enter "ContentGardener" as the name and click "Add New Application Password".</p>
                          <div className="mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                if (!currentWebsite?.url) return;
                                let cleanUrl = currentWebsite.url.trim();
                                if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
                                  cleanUrl = 'https://' + cleanUrl;
                                }
                                cleanUrl = cleanUrl.replace(/\/+$/, '');
                                window.open(`${cleanUrl}/wp-admin/profile.php`, '_blank');
                              }}
                            >
                              <Globe className="mr-2 h-4 w-4" /> Open WordPress Admin
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">3</div>
                        <div>
                          <h3 className="font-medium">Copy the Password</h3>
                          <p className="text-sm text-muted-foreground">WordPress will generate a password like "xxxx xxxx xxxx xxxx". Copy this password exactly as shown.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">4</div>
                        <div>
                          <h3 className="font-medium">Enter Credentials Below</h3>
                          <p className="text-sm text-muted-foreground">Use the "Connect & Test" button below to enter your WordPress username and the application password you just created.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 mt-6 pt-4 border-t">
                  <Button
                    variant="default"
                    className="w-full"
                    onClick={() => {
                      setShowInstructions(false);
                      setShowAuthDialog(true);
                    }}
                  >
                    <Link className="h-4 w-4 mr-2" /> Connect & Test
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowInstructions(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
